import React, { useEffect, useState } from "react";
import { Icon } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { produce } from "immer";
import { ModalBuilder, Form, ContentBuilder } from "@redriver/cinnamon";
import {
  PermissionCheck,
  UserTypeCheck,
} from "features/../../../shared/components/auth";
import { UserType } from "features/../../../shared/constants/enums";
import {
  Actions,
  Targets,
} from "features/../../../shared/constants/permissions";
import { TranslatedModal } from "features/../../../shared/components/modals";
import { DropdownItem, Grid, GridColumn } from "semantic-ui-react";
import { getMoveSheetData, moveSheet, validateCanMoveSheet } from "./actions";
import {
  DealerOfficesDropdownLookup,
  DealersDropdownLookup,
} from "features/../../../shared/features/Dealers/lookups";
import { ClientsDropdownLookup } from "features/Clients";
import {
  AgreementSchedulesTypeAheadLookup,
  ClientAgreementsTypeAheadLookup,
  DealerBoilerPlateTandCsDropdownLookup,
} from "features/Settings";
import { NonPropagatingInput } from "features/../../../shared/components/forms/NonPropagatingField";
import { setFilters } from "features/Agreements/AgreementsList";
import { initialState } from "features/Agreements/ReducerHelpers";

const MoveSheetModalContent = ({
  modalProps,
  formProps,
  sheet,
  getValidationMessage,
  t,
  state,
  setPageNavigation,
  setOnSubmittedFormData,
}) => {
  const [validationResult, setValidationResult] = useState(null);
  const { clientId } = formProps.value;

  const { sheetId, clientId: currentClientId } = sheet;

  const isDirty = state?.dirty ?? false;

  const submitDisabled = validationResult ? !validationResult.isValid : true;

  useEffect(() => {
    if (!clientId) {
      setValidationResult(null);
    }
  }, [clientId]);

  useEffect(() => {
    if (!isDirty) {
      setValidationResult(null);
    }
  }, [isDirty]);

  return (
    <TranslatedModal.Confirmation
      {...modalProps}
      header={t("HeaderText")}
      submitLabel={t("SubmitLabel")}
      submitDisabled={submitDisabled}
      className="move-sheet-modal"
    >
      <Form {...formProps}>
        <Grid>
          <GridColumn width={7}>
            <DealersDropdownLookup
              field="dealerId"
              textField="dealerName"
              label={t("FilterSupplier")}
              clearable
              placeholder={t("Supplier")}
              onKeyDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            />
            <DealerOfficesDropdownLookup
              className="move-sheet-margin"
              field="dealerOfficeId"
              textField="dealerOfficeName"
              label={t("FilterSupplierOffice")}
              clearable
              placeholder={t("SupplierOffice")}
              dealerId={formProps.value.dealerId}
              disabled={!formProps.value.dealerId}
              onKeyDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            />
            <ClientsDropdownLookup
              className="move-sheet-margin"
              field="clientId"
              textField="clientName"
              label={t("FilterCustomer")}
              clearable
              placeholder={t("Customer")}
              required
              dealerOfficeId={formProps.value.dealerOfficeId}
              disabled={!formProps.value.dealerOfficeId}
              onKeyDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            />
            {(formProps.value.clientId || formProps.value.dealerOfficeId) && (
              <>
                <Form.Trigger
                  field="dealerId"
                  event="onChange"
                  action="clear"
                  target="dealerOfficeId"
                />
                <Form.Trigger
                  field="dealerId"
                  event="onChange"
                  action="clear"
                  target="clientId"
                />
                <Form.Trigger
                  field="dealerOfficeId"
                  event="onChange"
                  action="clear"
                  target="clientId"
                />
              </>
            )}

            <Form.Trigger
              field="clientId"
              event="onChange"
              action="clear"
              target="clientAgreementId"
            />
            <Form.Trigger
              field="clientAgreementId"
              event="onChange"
              action="clear"
              target="agreementScheduleId"
            />
            <Form.Trigger
              field="clientId"
              event="onClear"
              action="clear"
              target="clientAgreementId"
            />
            <Form.Trigger
              field="clientAgreementId"
              event="onClear"
              action="clear"
              target="agreementScheduleId"
            />

            <div className="agreement-radio-buttons">
              <Form.RadioGroup
                field="newAgreement"
                label={t("NewAgreement")}
                options={[
                  { value: true, text: t("Yes") },
                  { value: false, text: t("No") },
                ]}
              />
            </div>
            <Form.If
              condition={({ fields }) =>
                fields.clientId && fields.newAgreement === false
              }
            >
              <ClientAgreementsTypeAheadLookup
                className="move-sheet-margin"
                label={t("MasterAgreement")}
                field="clientAgreementId"
                textField="clientAgreementName"
                clientId={formProps.value.clientId}
                placeholder={t("Agreements")}
                clearable
                required
                onKeyDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                search
              />
              {formProps.value.clientAgreementId && (
                <AgreementSchedulesTypeAheadLookup
                  className="move-sheet-margin"
                  label={t("Schedule")}
                  field="agreementScheduleId"
                  textField="agreementScheduleName"
                  agreementId={formProps.value.clientAgreementId}
                  placeholder={t("Schedules")}
                  clearable
                  required
                  disabled={!formProps.value.clientAgreementId}
                  onKeyDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  search
                />
              )}
            </Form.If>

            <Form.If
              condition={({ fields }) =>
                fields.clientId != null && fields.newAgreement === true
              }
            >
              <NonPropagatingInput
                field="masterProjectName"
                label={t("MasterProjectName")}
                required
              />
              <NonPropagatingInput
                field="scheduleProjectName"
                label={t("ScheduleProjectName")}
                required
              />
              {process.env.TRADE_AS_IPS && formProps.value.dealerId && (
                <DealerBoilerPlateTandCsDropdownLookup
                  label={t("TCsLabel")}
                  field="boilerPlateTandCsId"
                  dealerId={formProps.value.dealerId}
                  clearable
                  fluid
                  textField="boilerPlateTandCs"
                  className="agreement-terms-and-conditions"
                  onKeyDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
            </Form.If>

            <Form.If
              condition={({ fields }) =>
                fields.clientId && fields.clientId !== currentClientId
              }
            >
              <Form.Checkbox
                className="move-sheet-margin"
                field="navigateToNewCustomer"
                label={t("NavigateToNewCustomer")}
              />
            </Form.If>
            <Form.Trigger
              field="navigateToNewCustomer"
              event="onChange"
              action={(formState) => {
                setOnSubmittedFormData(formState.fields);
                setPageNavigation(formState.fields.navigateToNewCustomer);
              }}
            />
          </GridColumn>
          <GridColumn width={9}>
            {formProps.value.clientId &&
              formProps.value.dealerId &&
              formProps.value.dealerOfficeId &&
              isDirty && (
                <ContentBuilder
                  loadAction={validateCanMoveSheet}
                  loadParams={{ ...formProps.value, sheetId }}
                  onLoaded={(data) => {
                    setValidationResult(data);
                  }}
                  renderContent={(data, state) => {
                    if (
                      state.loading ||
                      (data && !data.isValid && !data?.checks?.length)
                    ) {
                      return null;
                    }

                    if (data?.isValid) {
                      return (
                        <p className="validation-success">
                          <Icon name="check circle" color="green" />{" "}
                          {t("ValidationSuccessText")}
                        </p>
                      );
                    }

                    const errorsExist =
                      Array.isArray(data.checks) &&
                      data.checks.some((c) => !c.hasWarning);
                    const warningsExist =
                      Array.isArray(data.checks) &&
                      data.checks.some((c) => c.hasWarning);

                    const headerKey = errorsExist
                      ? "sheet-errors"
                      : "sheet-warnings";
                    const listClass = errorsExist
                      ? "error-list"
                      : "warning-list";
                    const headerClass = errorsExist
                      ? "sheet-errors"
                      : "sheet-warnings";

                    return (
                      <div>
                        <p className={headerClass}>
                          <Icon
                            name={
                              errorsExist
                                ? "exclamation circle"
                                : "exclamation triangle"
                            }
                          />{" "}
                          {t(headerKey)}
                        </p>
                        <ul className={listClass}>
                          {Array.isArray(data.checks) &&
                            data.checks.map((check, index) => (
                              <li
                                key={index}
                                className={
                                  errorsExist && !check.hasWarning
                                    ? "error-item"
                                    : "warning-item"
                                }
                              >
                                {getValidationMessage(check)}
                              </li>
                            ))}
                        </ul>
                      </div>
                    );
                  }}
                />
              )}
          </GridColumn>
        </Grid>
      </Form>
    </TranslatedModal.Confirmation>
  );
};

const MoveSheetModal = ({ sheet, reloadSheetsForSchedule, onClientSelect }) => {
  const { t } = useTranslation("agreements", {
    keyPrefix: "SheetActions.MoveSheet",
  });
  const { t: t_enums } = useTranslation("enums");
  const dispatch = useDispatch();
  const { sheetId } = sheet;

  const getValidationMessage = (check) => {
    if (check.message) {
      return check.message;
    }
    const translationKey = `moveSheetErrors.${check.code}`;
    const translationOptions = {};
    if (check.items && check.items.length > 0) {
      translationOptions.items = check.items.join(", ");
    }
    const message = t_enums(translationKey, translationOptions);
    return message;
  };

  const [pageNavigation, setPageNavigation] = useState(false);
  const [onSubmittedFormData, setOnSubmittedFormData] = useState({});

  return (
    <UserTypeCheck restrictTo={UserType.HQ}>
      <PermissionCheck target={Targets.Sheet} action={Actions.Move}>
        <ModalBuilder
          withForm
          renderTrigger={(showModal) => (
            <DropdownItem onClick={showModal}>{t("TriggerText")}</DropdownItem>
          )}
          loadAction={getMoveSheetData}
          loadParams={sheetId}
          submitAction={moveSheet}
          submitParams={sheetId}
          onSubmitted={(result, formValues) => {
            if (pageNavigation) {
              const clientData = {
                id: onSubmittedFormData.clientId,
                name: onSubmittedFormData.clientName,
                code: onSubmittedFormData.clientCode,
                dealerOfficeId: onSubmittedFormData.dealerOfficeId,
                dealerOfficeName: onSubmittedFormData.dealerOfficeName,
                dealerId: onSubmittedFormData.dealerId,
                dealerName: onSubmittedFormData.dealerName,
              };

              onClientSelect(clientData);
              dispatch(
                setFilters(
                  produce(initialState.filters, (draftState) => {
                    draftState.search = onSubmittedFormData.newAgreement
                      ? onSubmittedFormData.masterProjectName
                      : onSubmittedFormData.clientAgreementName;
                  }),
                ),
              );
            } else {
              reloadSheetsForSchedule(result);
            }
          }}
          renderModal={(modalProps, formProps, state) => (
            <>
              <MoveSheetModalContent
                modalProps={modalProps}
                formProps={formProps}
                sheet={sheet}
                getValidationMessage={getValidationMessage}
                t={t}
                state={state}
                setPageNavigation={setPageNavigation}
                setOnSubmittedFormData={setOnSubmittedFormData}
              />
            </>
          )}
        />
      </PermissionCheck>
    </UserTypeCheck>
  );
};
export default MoveSheetModal;
